import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { CreditCard } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import type { ListStandardPayments } from '@virtual-care/client';
import {
  displayName,
  formatDateTime,
  formatDateTimeShort,
} from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  cn,
} from '@virtual-care/shadcn';
import { SearchInput, TableRowSort, client } from '@virtual-care/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';

type PaymentType = 'standard' | 'second_opinion' | 'homecare';

type PaymentRecord = ListStandardPayments['records'][number];

const endpointMap = {
  standard: 'GET /payments/standard/list',
  second_opinion: 'GET /payments/second-opinion/list',
  homecare: 'GET /payments/homecare/list',
} as const;

const statusColors: Record<string, string> = {
  PENDING:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  PAID: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  CAPTURED:
    'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  VOIDED: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
  AUTHORIZED:
    'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
  ABANDONED: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
  VERIFIED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
  CANCELED: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  EXPIRED: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  REFUNDED:
    'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 cursor-pointer hover:opacity-80',
  REFUND_PENDING:
    'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
};

const statusLabels: Record<string, string> = {
  PENDING: 'Pending',
  PAID: 'Paid',
  CAPTURED: 'Captured',
  FAILED: 'Failed',
  VOIDED: 'Voided',
  AUTHORIZED: 'Authorized',
  ABANDONED: 'Abandoned',
  VERIFIED: 'Verified',
  CANCELED: 'Canceled',
  EXPIRED: 'Expired',
  REFUNDED: 'Refunded',
  REFUND_PENDING: 'Refund Pending',
};

const allStatuses = Object.keys(statusLabels);

interface PaymentListProps {
  type: PaymentType;
  title: string;
}

export default function PaymentsList({ type, title }: PaymentListProps) {
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(
    null,
  );
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [showMoyasarDialog, setShowMoyasarDialog] = useState(false);

  const dataQuery = useQuery({
    queryKey: [
      'payments',
      type,
      pagination,
      sorting,
      statusFilter,
      searchQuery,
    ],
    queryFn: () => {
      return client.request(endpointMap[type], {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        status: statusFilter as any,
        query: searchQuery || undefined,
        sortBy: sorting.length > 0 ? (sorting[0].id as 'createdAt') : undefined,
        sortOrder:
          sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
      });
    },
    placeholderData: keepPreviousData,
  });

  const handleStatusClick = (payment: PaymentRecord) => {
    if (payment.status === 'REFUNDED') {
      setSelectedPayment(payment);
      setShowRefundDialog(true);
    }
  };

  const handleMoyasarClick = (payment: PaymentRecord) => {
    setSelectedPayment(payment);
    setShowMoyasarDialog(true);
  };

  const columns: ColumnDef<PaymentRecord>[] = [
    {
      id: 'patientName',
      header: 'Patient',
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original.user;
        if (!user) {
          return <div className="font-medium">N/A</div>;
        }
        return (
          <Link
            to={`/patients?viewPatient=${user.id}`}
            className="text-primary font-medium hover:underline"
          >
            {displayName(user)}
          </Link>
        );
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      enableSorting: false,
      cell: ({ row }) => {
        const amount = row.original.amount / 100;
        return (
          <div className="font-mono">
            {amount.toFixed(2)} {row.original.currency}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => {
        const payment = row.original;
        const isRefunded = payment.status === 'REFUNDED';
        return (
          <Badge
            className={cn('text-xs', statusColors[payment.status])}
            onClick={isRefunded ? () => handleStatusClick(payment) : undefined}
          >
            {statusLabels[payment.status]}
          </Badge>
        );
      },
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: 'Payment Date',
      cell: ({ row }) => (
        <div className="text-sm">
          {formatDateTime(new Date(row.original.createdAt))}
        </div>
      ),
    },
    {
      accessorKey: 'moyasarId',
      header: 'Moyasar ID',
      enableSorting: false,
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <button
            onClick={() => handleMoyasarClick(payment)}
            className="text-primary font-mono text-sm hover:underline"
          >
            {payment.moyasarId.substring(0, 12)}...
          </button>
        );
      },
    },
  ];

  const table = useReactTable({
    data: dataQuery.data?.records ?? [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      pagination,
      sorting,
    },
  });

  return (
    <div className="w-full">
      <CountablePageHeader
        title={title}
        totalCount={`${dataQuery.data?.totalCount ?? 0} total payments`}
        icon={CreditCard}
      />

      <div className="mb-4 flex items-center gap-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search by patient name..."
            value={searchQuery}
            onChange={setSearchQuery}
            onClear={() => setSearchQuery(undefined)}
            isLoading={dataQuery.isFetching}
          />
        </div>
        <Select
          value={statusFilter ?? 'all'}
          onValueChange={(v) => setStatusFilter(v === 'all' ? undefined : v)}
        >
          <SelectTrigger className="min-h-11 w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {allStatuses.map((status) => (
              <SelectItem key={status} value={status}>
                {statusLabels[status]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading payments...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No payments found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />

      {selectedPayment && (
        <>
          <RefundDetailsDialog
            payment={selectedPayment}
            open={showRefundDialog}
            onOpenChange={setShowRefundDialog}
          />
          <MoyasarDetailsDialog
            payment={selectedPayment}
            open={showMoyasarDialog}
            onOpenChange={setShowMoyasarDialog}
          />
        </>
      )}
    </div>
  );
}

interface RefundDetailsDialogProps {
  payment: PaymentRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function RefundDetailsDialog({
  payment,
  open,
  onOpenChange,
}: RefundDetailsDialogProps) {
  const refundedAmount = payment.refundedAmount
    ? payment.refundedAmount / 100
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Refund Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-muted-foreground text-sm">
                Refunded Amount
              </div>
              <div className="font-mono font-medium">
                {refundedAmount.toFixed(2)} {payment.currency}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-sm">
                Original Amount
              </div>
              <div className="font-mono font-medium">
                {(payment.amount / 100).toFixed(2)} {payment.currency}
              </div>
            </div>
          </div>
          {payment.refundedAt && (
            <div>
              <div className="text-muted-foreground text-sm">Refunded At</div>
              <div className="font-medium">
                {formatDateTimeShort(new Date(payment.refundedAt))}
              </div>
            </div>
          )}
          <div>
            <div className="text-muted-foreground text-sm">Refund Reason</div>
            <div className="font-medium">{payment.refundReason ?? 'N/A'}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface MoyasarDetailsDialogProps {
  payment: PaymentRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function MoyasarDetailsDialog({
  payment,
  open,
  onOpenChange,
}: MoyasarDetailsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Moyasar Payment Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-muted-foreground text-sm">Moyasar ID</div>
            <div className="font-mono text-sm break-all">
              {payment.moyasarId}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-muted-foreground text-sm">Amount</div>
              <div className="font-mono font-medium">
                {(payment.amount / 100).toFixed(2)} {payment.currency}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-sm">Status</div>
              <Badge className={cn('text-xs', statusColors[payment.status])}>
                {statusLabels[payment.status]}
              </Badge>
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-sm">Payment Date</div>
            <div className="font-medium">
              {formatDateTimeShort(new Date(payment.createdAt))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
